import { storage } from "../auth/storage";
import { readEnv, refreshToken as refreshTokenApi } from "../auth/oauth";
import { refreshTokenViaBackend } from "../services/backend/sfAuth";
import { getEnv } from "../config/getEnv";
import {
  incrementSfRefreshCount,
  isSfRefreshBudgetExhausted,
  isSfSessionExpired,
} from "../auth/sfSessionLimits";

let refreshPromise = null;

async function ensureTokenFresh() {
  const env = readEnv();
  const current = storage.readSfAuthSession();
  const refresh = current?.tokenSet?.refresh_token;
  const useBackend = Boolean(getEnv("REACT_APP_BE_URL"));
  if (!useBackend && !refresh) return;
  if (isSfSessionExpired() || isSfRefreshBudgetExhausted()) return;

  if (!refreshPromise) {
    const attempt = (async () => {
      let next;
      if (useBackend) {
        // One refresh call only (cookie and/or body); no chained .catch retry.
        next = await refreshTokenViaBackend({
          ...(refresh ? { refresh_token: refresh } : {}),
          auth_domain: env.SF_AUTH_DOMAIN,
          client_id: env.SF_CLIENT_ID,
        });
      } else {
        next = await refreshTokenApi(env, refresh);
      }
      incrementSfRefreshCount();
      storage.saveSfAuthSession({ ...(current ?? {}), tokenSet: next }, true);
    })();

    refreshPromise = attempt.finally(() => {
      refreshPromise = null;
    });
  }
  try {
    await refreshPromise;
  } catch {
    /* Leave session as-is; caller keeps original 401 / invalid session response. */
  }
}

function isAuthError(resp, body) {
  if (resp.status === 401) return true;
  if (body && typeof body === "object" && Array.isArray(body)) {
    return body.some((e) => e?.errorCode === "INVALID_SESSION_ID");
  }
  return false;
}

export async function sfFetch(input, init = {}) {
  const env = readEnv();
  const current = storage.readSfAuthSession();
  const token = current?.tokenSet?.access_token;
  const instanceUrl = current?.tokenSet?.instance_url ?? env.SF_AUTH_DOMAIN;
  const url = typeof input === "string" ? new URL(input, instanceUrl) : input;

  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const requestInit = { ...init, headers };

  const res1 = await fetch(url, requestInit);
  if (res1.ok) return res1;
  let body = null;
  try {
    body = await res1.clone().json();
  } catch {
    /* ignore */
  }
  if (!isAuthError(res1, body)) return res1;

  await ensureTokenFresh();
  const updated = storage.readSfAuthSession();
  const newToken = updated?.tokenSet?.access_token;
  if (!newToken) return res1;

  const headers2 = new Headers(init.headers);
  headers2.set("Authorization", `Bearer ${newToken}`);
  return fetch(url, { ...init, headers: headers2 });
}
